const encodeBase64UrlJson = (value: unknown): string => {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]);
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

function decodeBase64UrlJson<T>(encoded: string): T {
  let base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  if (pad) base64 += "=".repeat(4 - pad);

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json) as T;
}

/**
 * Parses an HTTP `Link` header and returns the URI of the entry whose
 * `rel` matches the given relation type.
 *
 * The header follows RFC 8288, e.g.
 *   <https://free.example.com/transfer/get/abc>; rel="alternate"
 * and may contain several comma-separated entries.
 *
 * @param linkHeader the raw value of the `Link` response header (may be undefined).
 * @param rel the relation type to look for (defaults to "alternate").
 * @returns the matching URI, or undefined if none is present.
 */
function getLinkHeaderUri(
  linkHeader: string | undefined | null,
  rel: string = "alternate"
): string | undefined {
  if (!linkHeader) return undefined;

  // Split into individual link entries. Commas inside the angle-bracketed URI
  // are not expected for our use case, so a simple split on "," is sufficient.
  for (const entry of linkHeader.split(",")) {
    const uriMatch = entry.match(/<([^>]*)>/);
    if (!uriMatch) continue;

    const relMatch = entry.match(/rel\s*=\s*"?([^";]+)"?/i);
    if (relMatch && relMatch[1].trim().toLowerCase() === rel.toLowerCase()) {
      return uriMatch[1].trim();
    }
  }

  return undefined;
}

/**
 * Builds the login URL for a rec-auth protected transfer.
 *
 * The recipient must authenticate before accessing the transfer. The login app
 * (skp-launchpad, served under `/account`) takes a `next` parameter telling it
 * where to redirect after a successful login — here, the transfer in the
 * authenticated web app (skp-client, served under `/transfer`).
 *
 * The origin is taken from the `Link: rel="alternate"` URL provided by
 * skp-server (which may differ from the sender's hostname); if that link is
 * missing or malformed, the current (sender's) hostname is used as a fallback.
 *
 * The `next` value is URI-encoded.
 *
 * Example:
 *   alternateTransferUrl: https://free.docker.teambeam.dev/transfer/get/abc
 *   result:               https://free.docker.teambeam.dev/account/auth?next=https%3A%2F%2Ffree.docker.teambeam.dev%2Ftransfer%2Ftransfers%2Freceived%2Fget%2Fabc
 *
 * @param transferUid the transfer's uid.
 * @param fallbackHostname hostname to use when no valid alternate link is given.
 * @param alternateTransferUrl the `rel="alternate"` transfer URL (may be undefined).
 * @returns the login URL to redirect the recipient to.
 */
function buildRecipientAuthLoginUrl(
  transferUid: string,
  fallbackHostname: string,
  alternateTransferUrl?: string
): string {
  let origin = `https://${fallbackHostname}`;

  if (alternateTransferUrl) {
    try {
      origin = new URL(alternateTransferUrl).origin;
    } catch {
      // Malformed alternate link — keep the fallback origin.
    }
  }

  const nextUrl = `${origin}/transfer/transfers/received/get/${transferUid}`;
  return `${origin}/account/auth?next=${encodeURIComponent(nextUrl)}`;
}

/**
 * Builds the login/signup URL for a space invitation.
 *
 * Both the auth page and the `next` return address live on the auth-next host,
 * since the launchpad drops a `next` pointing at a foreign host. `next` and
 * `hint` (the prefilled address) are encoded twice, because the launchpad
 * decodes both once more on top of the browser's own query decoding.
 *
 * @param nextHopFqdn the host to authenticate on, from auth/next.
 * @param spaceUid the invited-to space's uid.
 * @param invitee the invited e-mail address, raw.
 * @returns the auth URL to send the invitee to.
 */
function buildInvitationAuthUrl(
  nextHopFqdn: string,
  spaceUid: string,
  invitee: string
): string {
  const origin = `https://${nextHopFqdn}`;
  const nextUrl = `${origin}/invitation/${spaceUid}/${encodeURIComponent(
    invitee
  )}`;
  const next = encodeURIComponent(encodeURIComponent(nextUrl));
  const hint = encodeURIComponent(encodeURIComponent(invitee));
  return `${origin}/account/auth?next=${next}&hint=${hint}`;
}

export {
  buildInvitationAuthUrl,
  buildRecipientAuthLoginUrl,
  decodeBase64UrlJson,
  encodeBase64UrlJson,
  getLinkHeaderUri,
};
